import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { GlobalToastTypeNames, ToastTypes } from 'types';
import {
  useExpenseCategoriesQuery,
  useAddExpenseCategoryMutation,
  ExpenseCategoriesDocument,
} from 'core/graphql';

import { useToasts } from '../Toast/useToasts';
import { IExpenseCategory } from './types';

interface IExpenseCategoriesHook {
  categories: IExpenseCategory[];
  loading: boolean;
  createCategory: (
    name: string,
    slug: string,
  ) => Promise<IExpenseCategory | null>;
}

/** Slugify a category label client-side as a fallback. */
const slugify = (input: string): string =>
  input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .slice(0, 50);

export const useExpenseCategories = (): IExpenseCategoriesHook => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const { data, loading } = useExpenseCategoriesQuery({
    fetchPolicy: 'cache-and-network',
  });
  const [createMutation] = useAddExpenseCategoryMutation({
    refetchQueries: [{ query: ExpenseCategoriesDocument }],
    awaitRefetchQueries: true,
  });

  const categories = useMemo(
    () =>
      (data?.expenseCategories || []).filter((c): c is IExpenseCategory =>
        Boolean(c),
      ),
    [data?.expenseCategories],
  );

  const createCategory = useCallback(
    async (name: string, slug: string) => {
      const finalSlug = slug.trim() || slugify(name);
      try {
        const result = await createMutation({
          variables: { name, slug: finalSlug },
        });
        const payload = result.data?.createExpenseCategory;
        if (payload?.success && payload.category) {
          addToast({
            data: {
              type: ToastTypes.success,
              title: t('pages.fakturownia.toasts.categoryCreated'),
            },
            type: GlobalToastTypeNames.Expense,
          });
          return payload.category as IExpenseCategory;
        }
        addToast({
          data: {
            type: ToastTypes.error,
            title: t('pages.fakturownia.toasts.categoryFailed'),
            text: payload?.errors?.filter(Boolean).join('\n') || undefined,
          },
          type: GlobalToastTypeNames.Expense,
        });
        return null;
      } catch (error) {
        addToast({
          data: {
            type: ToastTypes.error,
            title: t('pages.fakturownia.toasts.categoryFailed'),
            text: (error as Error).message,
          },
          type: GlobalToastTypeNames.Expense,
        });
        return null;
      }
    },
    [createMutation, addToast, t],
  );

  return { categories, loading, createCategory };
};
